import React, { useState } from 'react';
import { Phone, Mail, MapPin, AlertTriangle, Loader2, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { openPreferences } from '@/lib/cookieConsent';
import GdprNotice from '@/components/GdprNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo.png";

const complaintTypes = [
  'Care Quality',
  'Staff Conduct',
  'Facilities',
  'Communication',
  'Billing',
  'Other',
];

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    complaint_type: '',
    description: '',
  });

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/complaints`, formData);
      toast.success('Your complaint has been submitted. We will respond within 48 hours.');
      setFormData({ name: '', email: '', phone: '', complaint_type: '', description: '' });
      setDialogOpen(false);
    } catch (error) {
      toast.error('Failed to submit complaint. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#1e3a5f] text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-4 mb-6">
              <img
                src={LOGO_URL}
                alt="Abney Low Nursing Home"
                className="h-24 w-auto"
              />
              <div>
                <div className="text-2xl font-bold">
                  Abney Low
                </div>
                <div className="text-sm text-white/80">Nursing Home</div>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed">
              Providing compassionate nursing, residential and dementia care in Hoylake, Wirral.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { label: 'About Us', href: '#about' },
                { label: 'Care Services', href: '#services' },
                { label: 'Facilities', href: '#facilities' },
                { label: 'CQC Rating', href: '#cqc' },
                { label: 'Reviews', href: '#reviews' },
                { label: 'Careers', href: '#careers' },
                { label: 'Contact', href: '#contact' },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    className="text-white/70 hover:text-[#c9a227] transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Care Services */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Our Care Services</h4>
            <ul className="space-y-3">
              {[
                'Nursing Care',
                'Residential Care',
                'Dementia Nursing',
                'Dementia Residential',
                'Mental Health Nursing',
                'Bariatric Nursing',
              ].map((service) => (
                <li key={service}>
                  <span className="text-white/70">{service}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#c9a227] flex-shrink-0 mt-0.5" />
                <span className="text-white/70">
                  17 Queen's Road,<br />
                  Hoylake, Wirral
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#c9a227]" />
                <a
                  href="tel:01516324504"
                  className="text-white/70 hover:text-[#c9a227] transition-colors"
                >
                  0151 632 4504
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#c9a227]" />
                <a
                  href="mailto:manager@abneylownursing.co.uk"
                  className="text-white/70 hover:text-[#c9a227] transition-colors"
                >
                  manager@abneylownursing.co.uk
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/50 text-sm">
            © {currentYear} Abney Low Nursing Home. Now newly operated by RSJB Nursing Homes.
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <Link to="/privacy" className="text-white/50 hover:text-white transition-colors" data-testid="footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/cookies" className="text-white/50 hover:text-white transition-colors" data-testid="footer-cookies-link">
              Cookies Policy
            </Link>
            <button
              onClick={openPreferences}
              className="text-white/50 hover:text-white transition-colors"
              data-testid="footer-cookie-prefs-btn"
            >
              Cookie Preferences
            </button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button
                  className="text-white/50 hover:text-[#c9a227] transition-colors flex items-center gap-1"
                  data-testid="complaints-btn"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Complaints
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg bg-white border border-slate-200 shadow-xl">
                <DialogHeader>
                  <DialogTitle className="text-2xl text-[#1e3a5f]">
                    Submit a Complaint
                  </DialogTitle>
                </DialogHeader>
                <p className="text-slate-600 text-sm mb-4">
                  We take all complaints seriously. Please provide details below and we will respond within 48 hours.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="complaint-form">
                  <div>
                    <Label htmlFor="complaint-name">Your Name *</Label>
                    <Input
                      id="complaint-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-2 rounded-xl"
                      data-testid="complaint-name-input"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="complaint-email">Email *</Label>
                      <Input
                        id="complaint-email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="mt-2 rounded-xl"
                        data-testid="complaint-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="complaint-phone">Phone</Label>
                      <Input
                        id="complaint-phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="mt-2 rounded-xl"
                        data-testid="complaint-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="complaint-type">Complaint Type *</Label>
                    <Select
                      value={formData.complaint_type}
                      onValueChange={(value) => setFormData({ ...formData, complaint_type: value })}
                    >
                      <SelectTrigger className="mt-2 rounded-xl" data-testid="complaint-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {complaintTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="complaint-desc">Description *</Label>
                    <Textarea
                      id="complaint-desc"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      className="mt-2 rounded-xl min-h-[120px]"
                      placeholder="Please describe your complaint in detail..."
                      data-testid="complaint-desc-input"
                    />
                  </div>
                  <GdprNotice testId="complaint-gdpr-notice" />
                  <Button
                    type="submit"
                    disabled={submitting || !formData.name || !formData.email || !formData.complaint_type || !formData.description}
                    className="w-full bg-[#1e3a5f] text-white hover:bg-[#162c4a] rounded-full py-3"
                    data-testid="complaint-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Submit Complaint
                      </>
                    )}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            <a href="/admin" className="text-white/50 hover:text-[#c9a227] transition-colors" data-testid="footer-admin-link">
              Admin
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
